import { useRef } from "react";
import Skeleton from "../../components/Skeleton";
import { AccountHolderRatePlan } from "../../services/accountService";
import RatePlanRow from "./RatePlanRow";
import { RatePlanType } from "./RatePlans";
import { sortStrings } from "../../utils/sorting";
import { useRatePlansContext } from "../../hooks/data/providers/ratePlansContext";

type Props = {
  accountHolderCode: string;
  accountHolderId: string;
  accountHolderRatePlans: AccountHolderRatePlan[];
  ratePlanType: RatePlanType;
  isMasterSurchargeEnabled?: boolean;
  isLoading: boolean;
  isEditing: boolean;
};

function RatePlanList({
  ratePlanType,
  isMasterSurchargeEnabled,
  accountHolderRatePlans,
  isLoading,
  isEditing,
}: Props): JSX.Element {
  // const { mutate: updateRatePlan, isLoading: isUpdatingRatePlan } =
  //   useUpdateRatePlanMutation(accountHolderCode);

  const { definedAccountHolderRatePlans: definedRatePlansCtx } =
    useRatePlansContext();

  const inputRefs = useRef<HTMLInputElement[]>([]);
  inputRefs.current = [];

  function addToInputRefs(el: HTMLInputElement) {
    if (el && !inputRefs.current.includes(el)) {
      inputRefs.current.push(el);
    }
  }

  // We need to know if at least 1 wallet is enabled
  const walletIsEnabled = accountHolderRatePlans.some(
    (rp) =>
      (rp.paymentMethodType === "googlepay" ||
        rp.paymentMethodType === "applepay") &&
      rp.allowedMethod === true
  );

  // function handleUpdateRatePlan() {
  //   if (currentEditingRatePlan) {
  //     updateRatePlan(currentEditingRatePlan.data!, {
  //       onSuccess() {
  //         openSnackbar(
  //           `Rate plan ${currentEditingRatePlan.label} was successfully updated.`,
  //           {
  //             type: "success",
  //           }
  //         );
  //         setCurrentEditingRatePlan(null);
  //       },
  //       onError() {
  //         openSnackbar("Failed to update rate plan. Please try again.", {
  //           type: "error",
  //         });
  //       },
  //     });
  //   }
  // }

  return (
    <div className="flex flex-col gap-y-4 py-4 pt-8">
      <div
        className={`${
          ratePlanType === "Flat Rate Plan" ? "grid-cols-8" : "grid-cols-7"
        } grid items-center gap-x-4 font-semibold max-sm:text-sm`}
      >
        <div></div>
        <div>Allowed</div>
        <div>
          Online {ratePlanType === "Flat Rate Plan" ? "Percentage" : "Markup"}
        </div>
        <div>Online Transaction</div>
        <div>
          In Person{" "}
          {ratePlanType === "Flat Rate Plan" ? "Percentage" : "Markup"}
        </div>
        <div>In Person Transaction</div>
        {ratePlanType === "Flat Rate Plan" && <div>Surcharge</div>}
      </div>
      {isLoading ? (
        <div className="flex flex-wrap gap-y-6">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="flex gap-x-6">
              <Skeleton className="w-20" />
              <Skeleton className="w-20" />
              <Skeleton className="w-20" />
              <Skeleton className="w-20" />
              <Skeleton className="w-20" />
              <Skeleton className="w-20" />
              <Skeleton className="w-20" />
            </div>
          ))}
        </div>
      ) : (
        definedRatePlansCtx
          .sort((a, b) => sortStrings(a.label, b.label))
          .map((ratePlan, i) => (
            <div key={ratePlan.label}>
              <RatePlanRow
                ratePlan={ratePlan}
                ratePlanType={ratePlanType}
                index={i}
                isEditing={isEditing}
                addToInputRefs={addToInputRefs}
                isMasterSurchargeEnabled={isMasterSurchargeEnabled ?? false}
                // If Google Pay and Apple Pay are both disabled at the master level,
                // disable the corresponding rate plan row if the type is interac since it can only be used in a wallet.
                // Right now we only care to do this with interac but is setup to handle others later if needed.
                rowDisabled={
                  !walletIsEnabled &&
                  ratePlan.data?.paymentMethodType === "interac_card"
                }
              />
            </div>
          ))
      )}
    </div>
  );
}

export default RatePlanList;
